import {
  CellContext,
  ColumnFiltersState,
  Row,
  SortingState,
  Table as TableType,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames';
import { KeyboardEvent, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Checkbox from '@/components/Checkbox/Checkbox';
import inputStyles from '@/components/Input/Input.module.css';
import Tooltip from '@/components/Tooltip/Tooltip';
import sharedTableStyles from '@/components/Tables/SharedTableStyles.module.css';
import SortIcon from '@/components/Tables/SortIcon';
import styles from './BundleUpchargesTable.module.css';
import { TableCardProps } from '@/components/Tables/TableCard/TableCard.types';
import { useTranslation } from 'react-i18next';
import { BundleUpchargeTableTitle } from './BundleUpchargeTableTitle';
import { BundleUpchargeStoreGroup } from '@/types/bundles.types';

const BundleUpchargesTable = <TData extends object>({
  breadcrumb,
  data,
  columns,
  enableFilters = false,
  enableRowSelection = false,
  enableMultiRowSelection = true,
  noRowsMessage,
  title,
  selectedRows,
  onRowSelectionChange,
  isLoading,
  itemIdentifier = 'none',
  accessTableApi,
  showPagination = false,
  initialSorting,
  meta,
}: TableCardProps<TData>) => {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState<SortingState>(initialSorting ?? []);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState({});
  const id = useId();

  // if we have an onRowSelectionChange prop, we assume selection state is being managed externally (via the selectedRows prop)
  const getRowSelectionHandler = () => {
    if (onRowSelectionChange) return onRowSelectionChange;
    return setRowSelection;
  };

  // if we receive selectedRows from props, update our internal state
  useEffect(() => {
    if (selectedRows) {
      setRowSelection(selectedRows);
    }
  }, [selectedRows]);

  const LoadingSkeleton = useCallback(
    ({ column }: CellContext<TData, unknown>) =>
      column.id === 'id' ? <div /> : <div className={sharedTableStyles.skeleton} data-testid="loading-skeleton-cell" />,
    [],
  );

  const renderCheckboxCell = useCallback(
    (params: { row: Row<unknown> }) => (
      <div className={styles.rowCheckbox}>
        <Checkbox
          label=""
          aria-label="select row"
          id={`${id}-row-${params.row.id}-checkbox`}
          checked={params.row.getIsSelected()}
          onChange={params.row.getToggleSelectedHandler()}
          disabled={!params.row.getCanSelect()}
        />
      </div>
    ),
    [id],
  );

  const renderHeaderCell = useCallback(
    (params: { table: TableType<unknown> }) => (
      <div className={styles.headerCheckbox}>
        <Checkbox
          label=""
          aria-label="select all rows"
          id={`${id}-header-checkbox`}
          checked={params.table.getIsAllRowsSelected()}
          onChange={params.table.getToggleAllRowsSelectedHandler()}
        />
      </div>
    ),
    [id],
  );

  const columnsWithRowSelection = useMemo(
    () => [
      {
        id: 'select',
        header: enableMultiRowSelection ? renderHeaderCell : undefined,
        cell: renderCheckboxCell,
      },
      ...columns,
    ],
    [columns, enableMultiRowSelection, renderCheckboxCell, renderHeaderCell],
  );

  const tableData = useMemo(() => (isLoading ? Array(30).fill({}) : data || []), [isLoading, data]);

  const tableColumns = useMemo(() => {
    if (isLoading) {
      return columns.map((column) => ({
        ...column,
        cell: LoadingSkeleton,
      }));
    }
    return enableRowSelection ? columnsWithRowSelection : columns;
  }, [isLoading, enableRowSelection, columnsWithRowSelection, columns, LoadingSkeleton]);

  const getPriceByOrderAndSort = (item: BundleUpchargeStoreGroup, sort: string, columnId: string) => {
    const ite = item.patches.find((patch) => patch.description === columnId);
    if (!ite || !ite.price) {
      return '';
    }
    if (!ite.price.split) {
      return `${ite.price}`;
    }
    const chosenPriceRange = ite.price.split('-') as unknown as string[];

    if (chosenPriceRange.length === 1) return Number(chosenPriceRange[0]);

    if (sort === 'asc') return Number(chosenPriceRange[0]);

    if (sort === 'desc') return Number(chosenPriceRange[1]);

    return Number(chosenPriceRange[0]);
  };

  const dynamicColumnSort = (
    rowA: Row<BundleUpchargeStoreGroup>,
    rowB: Row<BundleUpchargeStoreGroup>,
    columnId: string,
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const tableSortingState = table.getState().sorting;
    const tableSorting = tableSortingState.find((sort) => sort.id === columnId);
    const currentSort = tableSorting?.desc ? 'desc' : 'asc';
    const priceRangeA = getPriceByOrderAndSort(rowA.original, currentSort, columnId);
    const priceRangeB = getPriceByOrderAndSort(rowB.original, currentSort, columnId);

    if (Number.isNaN(priceRangeA) || Number.isNaN(priceRangeB)) return -1;

    const isEqual = priceRangeA === priceRangeB;
    if (isEqual) return 0;

    return priceRangeA > priceRangeB ? 1 : -1;
  };

  const table = useReactTable({
    data: tableData,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    enableFilters,
    enableRowSelection,
    enableMultiRowSelection,
    onColumnFiltersChange: setColumnFilters,
    sortingFns: {
      dynamicSort: dynamicColumnSort,
    },
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,
      rowSelection,
    },
    onSortingChange: setSorting,
    onRowSelectionChange: getRowSelectionHandler(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: showPagination ? getPaginationRowModel() : undefined,
    meta,
    debugTable: false,
  });

  useEffect(() => {
    if (accessTableApi) accessTableApi(table);
  }, [table, accessTableApi]);

  const getAriaSort = (sortStatus: 'asc' | 'desc' | false) => {
    switch (sortStatus) {
      case 'asc':
        return 'ascending';
      case 'desc':
        return 'descending';
      default:
        return 'none';
    }
  };

  const filteredRowCount = table.getFilteredRowModel().rows.length;

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [hasHorizontalScrollbar, setHasHorizontalScrollbar] = useState(false);
  const [hasVerticalScrollbar, setHasVerticalScrollbar] = useState(false);

  const checkForScrollbar = useCallback(() => {
    if (!tableContainerRef.current) return;
    if (tableContainerRef.current.clientWidth === tableContainerRef.current.scrollWidth) {
      setHasHorizontalScrollbar(false);
    }
    if (tableContainerRef.current.clientWidth < tableContainerRef.current.scrollWidth) {
      setHasHorizontalScrollbar(true);
    }
    if (tableContainerRef.current.clientHeight === tableContainerRef.current.scrollHeight) {
      setHasVerticalScrollbar(false);
    }
    if (tableContainerRef.current.clientHeight < tableContainerRef.current.scrollHeight) {
      setHasVerticalScrollbar(true);
    }
  }, []);

  useLayoutEffect(() => {
    checkForScrollbar();
  }, [isLoading, checkForScrollbar]);

  useEffect(() => {
    window.addEventListener('resize', checkForScrollbar);
    return () => window.removeEventListener('resize', checkForScrollbar);
  }, [checkForScrollbar]);

  return (
    <div className={styles.card}>
      <caption className={classNames(styles.tableTitle)}>
        <BundleUpchargeTableTitle breadcrumb={breadcrumb} title={title} data={undefined} columns={[]} />
      </caption>
      <div
        ref={tableContainerRef}
        className={classNames(styles.tableContainer, {
          [styles.noTitle]: title === undefined,
          [styles.withVerticalScrollBar]: hasVerticalScrollbar,
        })}
      >
        <table className={classNames(styles.table, { [styles.withRowSelection]: enableRowSelection })}>
          <thead className={styles.tableHead}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    data-testid={`table-header-${header.id}`}
                    key={header.id}
                    className={classNames(
                      styles.columnHeader,
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      //@ts-ignore
                      styles[header.column.columnDef.columnClassname],
                    )}
                    aria-sort={getAriaSort(header.column.getIsSorted())}
                  >
                    <div
                      data-testid={`table-sort-button-${header.id}`}
                      className={sharedTableStyles.columnHeaderTextAndIcon}
                      tabIndex={header.column.columnDef.enableSorting ? 0 : undefined}
                      aria-label={`sort column ${
                        typeof header.column.columnDef.header === 'string' ? header.column.columnDef.header : ''
                      }`}
                      onClick={
                        header.column.columnDef.enableSorting ? header.column.getToggleSortingHandler() : undefined
                      }
                      onTouchStart={
                        header.column.columnDef.enableSorting ? header.column.getToggleSortingHandler() : undefined
                      }
                      role="button"
                      onKeyDown={(event: KeyboardEvent) => {
                        switch (event.key) {
                          case ' ':
                          case 'Enter':
                            header.column.toggleSorting();
                            break;
                          default:
                            break;
                        }
                      }}
                    >
                      <span className={sharedTableStyles.columnHeaderText}>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </span>
                      {header.column.columnDef.enableSorting && <SortIcon />}
                      {header.column.columnDef.meta?.tooltipText !== undefined && (
                        <Tooltip content={header.column.columnDef.meta?.tooltipText} size={16} />
                      )}
                    </div>
                    {header.column.getCanFilter() ? (
                      <input
                        disabled={header.column.columnDef.meta?.disableFilter}
                        onChange={(event) => header.column.setFilterValue(event.target.value)}
                        value={(header.column.getFilterValue() as string) ?? ''}
                        className={classNames(inputStyles.input, styles.headerFilter)}
                        aria-label={`${header.column.columnDef.header} filter input`}
                        id={`${header.id}filterInput`}
                      />
                    ) : null}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, index) => (
              <tr
                key={row.original[itemIdentifier] || row.id}
                className={classNames(sharedTableStyles.row, {
                  [sharedTableStyles.even]: index % 2 === 0,
                  [sharedTableStyles.selectedRow]: row.getIsSelected(),
                })}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className={styles.cell} data-testid="row-data">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {!isLoading && filteredRowCount === 0 && (
            <caption className={styles.noRowsMessage}>{noRowsMessage ?? t('common.table.noData')}</caption>
          )}
        </table>
      </div>
      <div className={styles.footer} />
      {hasHorizontalScrollbar && <div className={sharedTableStyles.scrollShadow} />}
    </div>
  );
};

export default BundleUpchargesTable;
